/**
 * Normalizes clipboard HTML and editor DOM so pasted / legacy content does not
 * leave inline font-size or font-family that makes newly typed text look larger
 * or different from the editor defaults.
 */

/** Single typography for certificate editors (toolbar font controls are hidden). */
export const CERTIFICATE_EDITOR_FIXED_FONT_FAMILY = 'Arial, Helvetica, sans-serif'
export const CERTIFICATE_EDITOR_FIXED_FONT_SIZE = '14px'

const STRIP_STYLE_NAMES = new Set([
    'font-family',
    'font-size',
    'font',
    'color',
    'background-color',
    'letter-spacing',
    'line-height',
    'zoom',
    'transform',
    '-webkit-text-fill-color',
    'font-variation-settings',
    '-ms-zoom',
])

function stripCssDeclarations(styleStr) {
    if (!styleStr || typeof styleStr !== 'string') return ''
    return styleStr
        .split(';')
        .map((s) => s.trim())
        .filter(Boolean)
        .filter((rule) => {
            const i = rule.indexOf(':')
            if (i === -1) return false
            const name = rule.slice(0, i).trim().toLowerCase()
            if (name.startsWith('mso-')) return false
            if (STRIP_STYLE_NAMES.has(name)) return false
            return true
        })
        .join('; ')
}

function unwrapFontElements(root) {
    ;[...root.querySelectorAll('font')].forEach((font) => {
        const parent = font.parentNode
        if (!parent) return
        while (font.firstChild) parent.insertBefore(font.firstChild, font)
        parent.removeChild(font)
    })
}

function cleanElementTypography(el) {
    if (!el || el.nodeType !== Node.ELEMENT_NODE) return
    const tag = el.tagName
    el.removeAttribute('class')
    el.removeAttribute('lang')
    if (el.hasAttribute('style')) {
        const next = stripCssDeclarations(el.getAttribute('style'))
        if (next) el.setAttribute('style', next.endsWith(';') ? next : `${next};`)
        else el.removeAttribute('style')
    }
    el.removeAttribute('face')
    el.removeAttribute('size')
    el.removeAttribute('color')
    el.removeAttribute('bgcolor')
    if (tag === 'A') {
        const href = (el.getAttribute('href') || '').trim().toLowerCase()
        if (href.startsWith('javascript:')) el.removeAttribute('href')
    }
    if (tag === 'IMG') {
        el.remove()
    }
}

function unwrapBareSpans(root) {
    let again = true
    while (again) {
        again = false
        ;[...root.querySelectorAll('span')].forEach((span) => {
            if (span.attributes.length === 0) {
                const parent = span.parentNode
                if (!parent) return
                while (span.firstChild) parent.insertBefore(span.firstChild, span)
                parent.removeChild(span)
                again = true
            }
        })
    }
}

function sanitizePastedHtmlString(html) {
    if (!html || typeof html !== 'string') return ''
    try {
        const doc = new DOMParser().parseFromString(html, 'text/html')
        const body = doc.body
        if (!body) return ''

        body.querySelectorAll('script, style, iframe, object, embed, link, meta').forEach((n) => n.remove())

        unwrapFontElements(body)

        body.querySelectorAll('*').forEach((el) => cleanElementTypography(el))

        unwrapBareSpans(body)

        const tw = doc.createTreeWalker(body, NodeFilter.SHOW_COMMENT, null)
        const comments = []
        let c
        while ((c = tw.nextNode())) comments.push(c)
        comments.forEach((n) => n.parentNode?.removeChild(n))

        return body.innerHTML
    } catch (e) {
        return ''
    }
}

function selectionIsInsideEditor(editorEl) {
    if (!editorEl) return false
    const sel = window.getSelection()
    if (!sel || sel.rangeCount === 0) return false
    const range = sel.getRangeAt(0)
    const anc = range.commonAncestorContainer
    const node = anc.nodeType === Node.TEXT_NODE ? anc.parentNode : anc
    return node ? editorEl.contains(node) : false
}

/**
 * Walks the editor subtree and strips typography that should come from the editor
 * (font-size, font-family, line-height, etc.). Keeps alignment, bold/italic, lists.
 * Call after paste or when loading legacy HTML.
 *
 * @param {HTMLElement|null|undefined} editorEl
 */
export function normalizeCertificateEditorTypography(editorEl) {
    if (!editorEl) return

    unwrapFontElements(editorEl)

    editorEl.querySelectorAll('*').forEach((el) => {
        if (el.getAttribute('contenteditable') === 'false') {
            const st = el.getAttribute('style')
            if (!st) return
            const next = stripCssDeclarations(st)
            if (next) el.setAttribute('style', next.endsWith(';') ? next : `${next};`)
            else el.removeAttribute('style')
            el.removeAttribute('face')
            el.removeAttribute('size')
            el.removeAttribute('color')
            return
        }
        cleanElementTypography(el)
    })

    unwrapBareSpans(editorEl)
}

/**
 * @param {ClipboardEvent} e
 * @param {HTMLElement|null|undefined} editorEl contenteditable root
 */
export function handleCertificateRichTextPaste(e, editorEl) {
    if (!editorEl || !selectionIsInsideEditor(editorEl)) return

    const html = e.clipboardData?.getData('text/html')
    const plain = e.clipboardData?.getData('text/plain')

    e.preventDefault()
    editorEl.focus()

    if (html && html.trim()) {
        const cleaned = sanitizePastedHtmlString(html)
        const stripped = cleaned.replace(/<br\s*\/?>/gi, '').replace(/&nbsp;/gi, ' ').replace(/\s+/g, '').trim()
        if (stripped) {
            document.execCommand('insertHTML', false, cleaned)
            normalizeCertificateEditorTypography(editorEl)
            return
        }
    }
    if (plain != null) {
        document.execCommand('insertText', false, plain)
        normalizeCertificateEditorTypography(editorEl)
    }
}
